/**
 * User alias .pgp system (Faz 9.6, OCS alias.rs + ocad.pgp parity).
 *
 * File grammar is OCS/AutoCAD identical: one `ALIAS,*COMMAND` per line,
 * `;` comments, values trimmed and uppercased. The built-in pgp is EMBEDDED
 * (`DEFAULT_ALIASES_PGP`) and carries a version marker; user definitions
 * live in the persisted alias store (`yap-aliases`) and are exported to
 * `aliases.pgp` (appData backup on the desktop host through desktopIo).
 *
 * Versioned migration (OCS `migrate_aliases`): `VERSION_INTRODUCTIONS`
 * records the entries each YAP_ALIASES_VERSION added. When the stored
 * version is behind, the missing versions' entries are ADDED — but entries
 * the user overrode (same alias, own command) or deleted (removed list)
 * are skipped, so user intent survives upgrades.
 *
 * Resolution (OCS `resolve_alias`): only the FIRST token of a command line
 * is translated, arguments stay intact; alias→alias chains resolve through
 * the dispatcher with a depth guard. The chain sits at dispatcher step 1.5
 * (after the built-in parser) — user pgp → built-in pgp → extras, so the
 * hardcoded drafting aliases keep their precedence (plan §3.3).
 */

import { parseAcadPgp } from './acadPgp';
import { useAliasPgpStore, type AliasEntry } from '../state/useAliasPgpStore';
import { useCommandManager } from '../state/commandStore';
import { useAcadPgpStore } from '../state/useAcadPgpStore';
import { CANONICAL_COMMANDS, normalizeCommandName } from '../core/commands/canonicalCommands';

/** Current built-in pgp version — bump when VERSION_INTRODUCTIONS grows. */
export const YAP_ALIASES_VERSION = 1;

/** Store marker comment written by serializePgp (OCS ocad.pgp.version). */
export const YAP_ALIASES_MARKER = '; yap-aliases-version:';

/**
 * Built-in pgp (OCS assets/ocad.pgp subset, section banner format).
 * Kept deliberately compact: the hardcoded parser aliases (L, C, …) stay
 * authoritative for the built-ins, so this file mainly ships the AutoCAD
 * house set that the parser does NOT already own.
 */
export const DEFAULT_ALIASES_PGP = `; Yap built-in command aliases (pgp format)
${YAP_ALIASES_MARKER} ${YAP_ALIASES_VERSION}
;
; One alias per line:  ALIAS,*COMMAND   (comments start with ;)
; Overriding an entry in the alias editor keeps your change across
; version migrations; deleting one here hides it from this set.
;
; ---------- Undo / Redo / Save ----------
DDIM,*DIMSTYLE
AA,*AREA
DOS,*SHELL
LIST,*LI
LTS,*LTSCALE
XP,*EXPLODE
; ---------- Draw ----------
DT,*TEXT
; ---------- Modify ----------
RM,*REVMOVE
RO,*ROTATE
SCR,*SCRIPT
UN,*UNDO
; ---------- View ----------
Z,*ZOOM
P,*PAN
RE,*REGEN
; ---------- Blocks / Insert ----------
B,*BLOCK
I,*INSERT
; ---------- Layers ----------
LA,*LAYER
LW,*LINEWEIGHT
; ---------- Plot / File ----------
PR,*PLOT
`;

/** Entries introduced by each pgp version (v1 = the built-in set itself). */
const VERSION_INTRODUCTIONS: Record<number, Array<{ alias: string; command: string }>> = {
    1: parsePgpEntries(DEFAULT_ALIASES_PGP),
};

/** Parses a pgp text into clean entries (empty lines/comments dropped). */
export function parsePgpEntries(text: string): AliasEntry[] {
    return parseAcadPgp(text)
        .filter((e) => e.alias && e.command)
        .map((e) => ({ alias: e.alias, command: e.command }));
}

/**
 * OCS `migrate_aliases`: adds every version's introduced entries that the
 * user neither overrode nor deleted. Returns the merged set + new version.
 */
export function migrateAliases(
    stored: AliasEntry[],
    storedVersion: number,
    removed: string[]
): { entries: AliasEntry[]; version: number } {
    if (storedVersion >= YAP_ALIASES_VERSION) {
        return { entries: stored, version: storedVersion };
    }
    const byAlias = new Map(stored.map((e) => [e.alias.toUpperCase(), { ...e }]));
    const removedSet = new Set(removed.map((a) => a.toUpperCase()));
    for (let v = storedVersion + 1; v <= YAP_ALIASES_VERSION; v++) {
        for (const intro of VERSION_INTRODUCTIONS[v] ?? []) {
            const key = intro.alias.toUpperCase();
            if (byAlias.has(key)) continue; // user override survives
            if (removedSet.has(key)) continue; // user deletion survives
            byAlias.set(key, { alias: intro.alias, command: intro.command });
        }
    }
    return { entries: [...byAlias.values()], version: YAP_ALIASES_VERSION };
}

/**
 * One-time lazy migration (store boots at version 0 → built-in set v1).
 * zustand persist rehydrates synchronously from localStorage, so the first
 * resolve/editor open sees the persisted state.
 */
let migrationChecked = false;
export function ensureAliasStoreMigrated(): void {
    if (migrationChecked) return;
    migrationChecked = true;
    const { version, entries, removed, setStore } = useAliasPgpStore.getState();
    if (version < YAP_ALIASES_VERSION) {
        const result = migrateAliases(entries, version, removed);
        setStore({ entries: result.entries, removed, version: result.version });
    }
}

/**
 * Resolution chain for one typed token: user pgp → built-in pgp → the pgp
 * import extras (SAVE, QSAVE…). Null when no alias claims the name.
 */
export function resolveAliasPgp(name: string): string | null {
    ensureAliasStoreMigrated();
    const key = normalizeCommandName(name);
    if (!key) return null;
    const { entries } = useAliasPgpStore.getState();
    const hit = entries.find((e) => normalizeCommandName(e.alias) === key);
    if (hit) return hit.command;
    const extras = useAcadPgpStore.getState().extras;
    const extra = extras.find((e) => normalizeCommandName(e.alias) === key);
    if (extra) return extra.legacyId;
    return null;
}

/**
 * Rewrites the first token of a raw line with the alias target (arguments
 * preserved — OCS "only the first token is translated").
 */
export function rewriteFirstToken(input: string, target: string): string {
    const trimmed = input.trim();
    const rest = trimmed.slice(trimmed.indexOf(' '));
    return rest.trim() ? `${target} ${rest.trim()}` : target;
}

/**
 * Known command names for the alias editor's unknown-command check:
 * canonical name pools + legacy registry ids/labels + pgp extras targets.
 */
export function getKnownAliasTargetNames(): Set<string> {
    const known = new Set<string>();
    for (const cmd of CANONICAL_COMMANDS) {
        for (const pool of [cmd.names['2d'], cmd.names['3d']]) {
            for (const name of pool) known.add(normalizeCommandName(name));
        }
        known.add(normalizeCommandName(cmd.id));
        known.add(normalizeCommandName(cmd.label));
    }
    const registry = useCommandManager.getState().commands;
    for (const id of Object.keys(registry)) {
        known.add(normalizeCommandName(id));
        known.add(normalizeCommandName(registry[id].label ?? ''));
    }
    for (const extra of useAcadPgpStore.getState().extras) {
        known.add(normalizeCommandName(extra.alias));
        known.add(normalizeCommandName(extra.legacyId));
    }
    return known;
}

/** Duplicate-alias rows (OCS `duplicate_conflicts`). */
export function findDuplicateAliases(entries: AliasEntry[]): Set<string> {
    const seen = new Map<string, number>();
    for (const e of entries) {
        const key = normalizeCommandName(e.alias);
        seen.set(key, (seen.get(key) ?? 0) + 1);
    }
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k));
}

/** Built-in (canonical) command-name index for shadowing flags. */
const BUILTIN_NAME_SET = new Set<string>();
for (const cmd of CANONICAL_COMMANDS) {
    for (const pool of [cmd.names['2d'], cmd.names['3d']]) {
        for (const name of pool) {
            const key = normalizeCommandName(name);
            if (key) BUILTIN_NAME_SET.add(key);
        }
    }
}

/**
 * True when an alias token collides with a built-in command name — the
 * dispatcher's parser layer resolves built-ins first, so such aliases are
 * inert unless their target matches the built-in (flagged in ALIASEDIT).
 */
export function isBuiltinCommandName(name: string): boolean {
    return BUILTIN_NAME_SET.has(normalizeCommandName(name));
}

/** OCS `to_pgp`: header comments, alias-sorted, `{alias},` at 10 columns. */
export function serializePgp(entries: AliasEntry[], version: number): string {
    const header = [
        '; Yap user command aliases (pgp format)',
        `${YAP_ALIASES_MARKER} ${version}`,
        '; Generated by ALIASEDIT — edit here or in the alias editor.',
    ];
    const rows = [...entries]
        .filter((e) => e.alias && e.command)
        .sort((a, b) => a.alias.localeCompare(b.alias))
        .map((e) => `${e.alias},`.padEnd(10) + `*${e.command}`);
    return [...header, ...rows, ''].join('\n');
}
